import { IOrganizationProductInstance, OrganizationProduct } from "../domain/OrganizationProduct";
import { ProductAttribute } from "../domain/ProductAttribute";
import {
    ADMINCENTER_PRODUCT_ID,
    ALL_PROJECTS_ID,
    ATTRIBUTE_SCOPES,
    CUSTOMER_DEFINED_INSTANCE_ROLE,
    CUSTOMER_DEFINED_PRODUCT_ROLE,
    SYSTEM_ROLE_TYPE,
    GROUP_TYPES
} from "../constants/constants";
import { ProductInstance } from "../domain/ProductInstance";
import { Role } from "../domain/Role";
import { ProductPermission } from "../domain/Permission.interface";
import { AccessDetailsByInstanceId, AccessDetailsByProductId, AccessDetailsRow } from "../domain/AccessDetails";
import { UserGroup } from "../domain/UserGroup";
import { InstanceRole } from "../domain/InstanceRole.interface";
import { ERROR_KEYS } from "../constants/data-dog-constants";
import { NATIVE_INSTANCE_ID_PROPERTY_NAME } from "../constants/property-constants";

export type UserGroupWithProjectDetails = Partial<UserGroup> & {
    // TODO: This should only need to support the array but with our current implementations
    // support for both is the easiest to accomodate all use cases with least refactoring.
    // NOTE: This is strictly for analytics at the time of writing :)
    projectId?: string;
    projectName?: string;
    projectIds?: string[];
    projectNames?: string[];
};

export type ProductGroupMatch = { group: UserGroup; role: Role; project?: ProductAttribute };

export type ProductGroupResolution =
    | { status: "resolved"; match: ProductGroupMatch }
    | { status: "none"; candidateGroupIds: [] }
    | { status: "ambiguous"; candidateGroupIds: string[] };

/**
 * Builds the name Turnstile gives a native product group: "{role name} - {instance nickname}".
 * The role's backend `name` is used here, not `displayName`, because that is what the group name
 * is generated from.
 */
export const getNativeProductGroupName = ({ instance, role }: { instance: IOrganizationProductInstance; role: Role }) =>
    `${role.name} - ${instance.nickname || instance.name}`;

/**
 * Resolves the single product group that should receive a user when an admin grants
 * `role` on `instance`.
 *
 * Role linking (EPTS-5258) stamps one product's roles onto another product's groups, so a
 * (instance, role) pair can be offered by many product groups at once — for example every linked
 * Experimentation group offers the Analytics "User" role. Matching on role id alone therefore
 * resolves the ambiguity by whatever order the API happened to return, which silently grants far
 * more access than the admin selected (EPTS-6364).
 *
 * Candidates are scoped to the selected instance first — role ids are product-global, so the same
 * id appears on sibling instances — and then to the selected project. orgPermissionListToAccessList
 * emits one entry per (group, role, project), so without the project scope a role picked for one
 * project competes against groups that only grant it on a sibling project. When more than one
 * candidate remains, the native group is identified by the backend's `nativeInstanceId` property,
 * falling back to its generated name for orgs the backfill has not reached. Anything that cannot be
 * resolved to exactly one group is reported back rather than guessed at, so the caller can fail
 * visibly and write nothing.
 *
 * This is a correctness guard on what the admin is shown and asked to confirm, not an authorization
 * boundary — the grant itself is still authorized server-side.
 *
 * `projectId` is the caller's current project selection:
 *   - `undefined`      — the caller has no project dimension (non-Experimentation products, or an
 *                        instance-level row). Project is not considered.
 *   - `ALL_PROJECTS_ID` — entries with no project, or whose role is an instance (account-level)
 *                        role. An Experimentation account role carries every project's key, so it
 *                        is only ever emitted per-project and would otherwise never qualify.
 *   - a project id      — only entries for that project qualify.
 */
export const resolveProductGroupForRole = ({
    instance,
    instanceAccess,
    projectId,
    role
}: {
    instance: IOrganizationProductInstance;
    instanceAccess: ProductPermission["instanceAccess"] | undefined;
    projectId?: string;
    role: Role;
}): ProductGroupResolution => {
    const matchesSelectedProject = (ar: ProductGroupMatch) => {
        if (projectId === undefined) return true;
        if (projectId !== ALL_PROJECTS_ID) return ar.project?.id === projectId;

        // "All Projects" means the account-level grant. An Experimentation account role carries
        // every project's key as an attribute, so associateGroupRoles emits it once per project and
        // never project-less — requiring !project would reject all of them and fail closed on the
        // one group that is actually correct (EPTS-6437). The role picker already decides what
        // "All Projects" offers by isInstanceRole, so match on that here and keep the two in step.
        return !ar.project || !!ar.role.isInstanceRole;
    };

    const matches =
        instanceAccess
            ?.filter((ia) => ia.instance?.id === instance.id)
            .flatMap((ia) => ia.availableRoles)
            .filter(
                (ar) =>
                    ar.role.id === role.id && ar.group.groupType === GROUP_TYPES.PRODUCT && matchesSelectedProject(ar)
            ) || [];

    // These are role associations, not groups: orgPermissionListToAccessList emits one entry per
    // (group, role, project), so an Experimentation group whose role spans several projects shows
    // up once per project on the same instance. Collapse to distinct groups before counting, or a
    // perfectly unambiguous single-group grant reads as ambiguous.
    const seenGroupIds = new Set<string>();
    const candidates = matches.filter((ar) => {
        if (seenGroupIds.has(ar.group.id)) {
            return false;
        }
        seenGroupIds.add(ar.group.id);
        return true;
    });

    if (!candidates.length) {
        return { status: "none", candidateGroupIds: [] };
    }

    if (candidates.length === 1) {
        return { status: "resolved", match: candidates[0] };
    }

    // The backend stamps a native product group with the instance it belongs to (EPTS-6348):
    // provisioning sets it on every native group it creates, and the 2.111.0 backfill stamped any
    // existing group matching the generated name shape — linked or not. So the marker says "native
    // to this instance", nothing about role linking, and its absence means only that the group was
    // never stamped: Experimentation project groups ("{role} - {project} - {projectId}") and
    // account groups ("{nickname} - Account - {role}") do not match the shape and carry nothing.
    const nativeInstanceIdOf = (ar: ProductGroupMatch) =>
        ar.group.properties?.find((p) => p.name === NATIVE_INSTANCE_ID_PROPERTY_NAME)?.value;

    // Prefer the group the backend marked as native to the selected instance. This beats the
    // generated-name guess below, whose shape only holds for products that build it as
    // "{role} - {nickname}" — Experimentation does not, so there it matches nothing (EPTS-6616).
    const nativeByProperty = candidates.filter((ar) => nativeInstanceIdOf(ar) === instance.id);

    if (nativeByProperty.length === 1) {
        return { status: "resolved", match: nativeByProperty[0] };
    }

    // Several groups each claiming to be native to this instance is contradictory data, and the
    // marker outranks the name guess below. Falling through would let an unmarked lookalike that
    // happens to carry the generated name beat every marked group, so report it instead.
    if (nativeByProperty.length > 1) {
        return { status: "ambiguous", candidateGroupIds: candidates.map((ar) => ar.group.id) };
    }

    // A candidate naming a *different* instance as its native one belongs to that instance and is
    // only reachable here through role linking (EPTS-5258). Granting it would also hand over the
    // permissions it carries at home — assign Experimentation and silently get Analytics as well.
    // With several candidates competing, drop the ones explicitly native elsewhere. A lone
    // candidate already resolved above, so a role only a linked group offers here still works.
    //
    // Testing for an absent marker is enough here, and only because of the two returns above: any
    // candidate marked native to this instance has already been handled, so none is left to keep.
    // Reinstate the `=== instance.id` arm if either of those returns ever goes away.
    const notNativeElsewhere = candidates.filter((ar) => !nativeInstanceIdOf(ar));

    if (notNativeElsewhere.length === 1) {
        return { status: "resolved", match: notNativeElsewhere[0] };
    }

    // Fallback for groups that were never stamped — chiefly an environment where the 2.111.0
    // backfill has not been deployed, since there a native group carries no marker and would
    // otherwise regress from a working grant to a fail-closed error. It finds only groups matching
    // the generated name, which is the same shape the backfill keys on, so it adds nothing once
    // that has run. Scoped to the candidates not native elsewhere even when that set is empty, so a
    // group belonging to another instance can never win on name shape alone: when every candidate
    // belongs elsewhere there is nothing here to grant, and the admin is told.
    const nativeGroupName = getNativeProductGroupName({ instance, role });
    const nativeCandidates = notNativeElsewhere.filter((ar) => ar.group.name === nativeGroupName);

    if (nativeCandidates.length === 1) {
        return { status: "resolved", match: nativeCandidates[0] };
    }

    return {
        status: "ambiguous",
        candidateGroupIds: candidates.map((ar) => ar.group.id)
    };
};

/**
 * Maps an unresolved resolution to its error key. "none" (no product group offers the role on the
 * instance) is a configuration gap; "ambiguous" (several groups compete) is a role-link conflict.
 * They are reported under separate keys so a DataDog alert on one does not mix in the other.
 */
export const getProductGroupResolutionErrorKey = (
    status: Exclude<ProductGroupResolution["status"], "resolved">
): string => {
    switch (status) {
        case "none":
            return ERROR_KEYS.ACCESS_GRANT.NO_PRODUCT_GROUP;
        case "ambiguous":
            return ERROR_KEYS.ACCESS_GRANT.AMBIGUOUS_PRODUCT_GROUP;
        default: {
            // A new unresolved status must be given its own key rather than falling back to one of
            // the above and filing the wrong alert. This fails the build until it is.
            const unhandled: never = status;
            return unhandled;
        }
    }
};

export const getProductFromInstanceId = ({
    instanceId,
    products
}: {
    instanceId?: string;
    products: OrganizationProduct[];
}) => {
    if (!instanceId) return undefined;

    for (const product of products) {
        if (product.instances?.some((instance) => instance.id === instanceId)) {
            return product;
        }
    }

    return undefined;
};

export const getInstanceFromProjectId = ({
    instances,
    projectId,
    projects = []
}: {
    instances?: IOrganizationProductInstance[];
    projectId: string;
    projects?: ProductAttribute[];
}) => {
    const project = projects?.find((p) => p.id === projectId);
    return instances?.find((i) => i.id === project?.instanceId);
};

export const findByField = ({ field, items, value }: { field: string; items: any[]; value: string }) => {
    return items?.find((i) => i[field] === value);
};

export const getEntitledProductRoles = ({
    products,
    instances,
    roles
}: {
    products: OrganizationProduct[];
    instances: ProductInstance[];
    roles: Role[];
}): Role[] => {
    // Step 1: Extract productIds from the products that the current Org is entitled to.
    const productIds = products?.map((pd) => pd.id);
    const instanceIds = instances?.map((i) => i.instanceId);

    // Step 2: Extract the roles that correspond to the Org's products
    return roles?.filter(
        (role) =>
            role.attributes.some(
                (attr) =>
                    attr.scopes?.some(
                        (sc) =>
                            (sc.name === ATTRIBUTE_SCOPES.PRODUCT && productIds?.includes(sc.value)) ||
                            (sc.name === ATTRIBUTE_SCOPES.INSTANCE && instanceIds?.includes(sc.value)) ||
                            sc.name === ATTRIBUTE_SCOPES.PROJECT
                        // If there is any role with scope = projectId, it means org is entitled to EXP, so no need to check for value.
                    )
            ) ||
            role.attributes.every(
                (attr) => (attr.scopes ?? []).length === 0 // Check if all scopes arrays are empty, these are admin center roles that are not scoped to any product.
            )
    );
};

/**
 * The set of instance ids the org is entitled to, for filtering entities that carry an instance id.
 *
 * Prefer this over `getEntitledProductRoles` when the entity being filtered already names an
 * instance: the org's instance list arrives complete in a single unpaginated response, whereas the
 * role list is fetched with a page cap that large orgs exceed — roles past the cap read as
 * unentitled and silently hide their entities.
 *
 * `instances` is optional because the caller's SWR hook returns `undefined` until the request
 * resolves; an unloaded list yields an empty set rather than throwing.
 */
export const getEntitledInstanceIds = ({ instances }: { instances?: ProductInstance[] }): Set<string> => {
    return new Set((instances ?? []).map((instance) => instance.instanceId));
};

export const getSystemRoles = ({
    roles,
    projectId,
    productId
}: {
    roles: Role[];
    projectId?: string;
    productId?: string;
}) => {
    let systemRoles = roles.filter((r) => {
        const { roleType, isInstanceRole, isSystemGeneratedRole, name } = r;
        const isSystemRole = roleType === SYSTEM_ROLE_TYPE;
        if (projectId) {
            if (projectId === ALL_PROJECTS_ID) {
                return isInstanceRole;
            } else {
                return isSystemGeneratedRole;
            }
        } else {
            const isCustomerDefinedSystemRole =
                [CUSTOMER_DEFINED_PRODUCT_ROLE, CUSTOMER_DEFINED_INSTANCE_ROLE].indexOf(roleType) > -1 &&
                isSystemGeneratedRole;
            const isProjectOwnerRole = name?.includes("Project Owner");
            // Filter out User Manager roles for Admin Center product only
            const isUserManagerRole = productId === ADMINCENTER_PRODUCT_ID && name?.startsWith("User Manager - ");
            return isSystemRole || (isCustomerDefinedSystemRole && !isProjectOwnerRole && !isUserManagerRole);
        }
    });

    return systemRoles;
};

export const getAccessByProduct = ({
    productInstances,
    userGroup,
    isCustomGroup
}: {
    productInstances: ProductInstance[];
    userGroup: UserGroup;
    isCustomGroup: boolean;
}) => {
    const accessDetails = productInstances.reduce(
        (accessDetails: AccessDetailsByProductId, instance: ProductInstance) => {
            const { productId, productName, instanceId, instanceName, nickname, roles } = instance;

            const instanceDetailsRow: AccessDetailsRow = {
                ...instance,
                instanceName,
                nickname: nickname ?? undefined,
                roles,
                isCustomGroup
            };

            if (!!userGroup.id) instanceDetailsRow.groupId = userGroup.id;
            if (!!userGroup.name) instanceDetailsRow.groupName = userGroup.name;

            if (!accessDetails[productId]) {
                const instanceAccess = {} as AccessDetailsByInstanceId;
                instanceAccess[instanceId] = [instanceDetailsRow];

                accessDetails[productId] = { productName, instanceAccess };
            } else {
                if (!accessDetails[productId].instanceAccess[instanceId]) {
                    accessDetails[productId].instanceAccess[instanceId] = [instanceDetailsRow];
                } else {
                    accessDetails[productId].instanceAccess[instanceId].push(instanceDetailsRow);
                }
            }

            return accessDetails;
        },
        {} as AccessDetailsByProductId
    );
    return accessDetails;
};

export const updateAccessWithProjectDetails = ({
    productList,
    availableOrgAccess,
    groupId
}: {
    productList: AccessDetailsByProductId;
    availableOrgAccess: ProductPermission[] | undefined;
    groupId: string;
}) => {
    const matchingOrgExperimentationRoles = availableOrgAccess
        ?.find((g) => g.productId === process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID)
        ?.instanceAccess.flatMap((ia) => ia.availableRoles)
        .filter((role) => role.group.id === groupId);

    const groupExperimentationAccess = productList[process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID!]?.instanceAccess;
    const updatedGroupExperimentationAccess = { ...groupExperimentationAccess } as AccessDetailsByInstanceId;

    const groupExperimentationInstanceIds = Object.keys(groupExperimentationAccess);

    groupExperimentationInstanceIds.forEach((groupExperimentationInstanceId) => {
        groupExperimentationAccess[groupExperimentationInstanceId].forEach(
            (groupExperimentationInstanceAccess, instanceIndex) => {
                const uniqueProjects: { [key: string]: string } = {};

                if (groupExperimentationInstanceAccess.roles.length > 0) {
                    groupExperimentationInstanceAccess.roles.forEach((role) => {
                        matchingOrgExperimentationRoles?.find((matchingRole) => {
                            if (matchingRole.role.id === role.id && !!matchingRole.project) {
                                return (uniqueProjects[matchingRole?.project?.id] = matchingRole?.project?.name);
                            }
                            return null;
                        });
                    });
                }

                Object.keys(uniqueProjects).forEach((projectId, projectIndex) => {
                    if (projectIndex === 0) {
                        updatedGroupExperimentationAccess[groupExperimentationInstanceId][instanceIndex].projectId =
                            projectId;
                        updatedGroupExperimentationAccess[groupExperimentationInstanceId][instanceIndex].projectName =
                            uniqueProjects[projectId];
                    } else {
                        updatedGroupExperimentationAccess[groupExperimentationInstanceId].push({
                            ...groupExperimentationInstanceAccess,
                            projectId,
                            projectName: uniqueProjects[projectId]
                        });
                    }
                });
            }
        );

        const updatedProductList = { ...productList } as AccessDetailsByProductId;
        updatedProductList[process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID!].instanceAccess =
            groupExperimentationAccess;
        return updatedProductList;
    });

    const updatedProductList = { ...productList } as AccessDetailsByProductId;
    updatedProductList[process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID!].instanceAccess =
        updatedGroupExperimentationAccess;
    return updatedProductList;
};

export const hasExperimentationAccessDetails = ({ accessDetails }: { accessDetails: AccessDetailsByProductId }) => {
    return !!accessDetails[process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID!];
};

export const getExperimentationProjectNamesAndIds = ({
    accessDetails
}: {
    accessDetails: AccessDetailsByProductId;
}) => {
    const experimentationAccess = accessDetails[process.env.REACT_APP_EXPERIMENTATION_PRODUCT_ID!];
    if (!experimentationAccess) {
        return [[], []];
    }
    const instanceIds = Object.keys(experimentationAccess.instanceAccess);
    let projectIds: string[] = [],
        projectNames: string[] = [];
    instanceIds?.forEach((instanceId) => {
        const flattenedAccess = experimentationAccess.instanceAccess[instanceId].flat();
        flattenedAccess?.forEach((item) => {
            if (item.projectId && projectIds.indexOf(item.projectId) === -1) {
                projectIds.push(item.projectId);
                projectNames.push(item.projectName || "");
            }
        });
    });
    return [projectIds, projectNames];
};

/**
 * Converts access data from AccessManagementUserForm (ProductPermission[]) to AccessDetailsByProductId format
 * @param access - Array of ProductPermission from useUserAccessList hook
 * @returns AccessDetailsByProductId - Nested structure grouped by product and instance
 */
export const convertAccessToAccessDetailsByProductId = (
    access: ProductPermission[] | undefined
): AccessDetailsByProductId => {
    if (!access || access.length === 0) {
        return {};
    }

    const accessDetails: AccessDetailsByProductId = {};

    access.forEach((productPermission) => {
        const { productId, productName, instanceAccess } = productPermission;

        if (!productId || !productName) {
            return; // Skip if missing required product info
        }

        // Initialize the product entry if it doesn't exist
        if (!accessDetails[productId]) {
            accessDetails[productId] = {
                productName,
                instanceAccess: {}
            };
        }

        // Process each instance in the product
        instanceAccess?.forEach(({ instance, availableRoles }) => {
            const instanceId = instance.id;
            const instanceName = instance.name;
            const nickname = instance.nickname;

            // Group roles by group and project to create proper AccessDetailsRow entries
            const groupedByGroupAndProject: { [key: string]: AccessDetailsRow } = {};

            availableRoles.forEach(({ group, role, project }) => {
                // Create a unique key for group + project combination
                const groupKey = `${group.id || "no-group"}`;
                const projectKey = project ? `_${project.id}` : "";
                const key = `${groupKey}${projectKey}`;

                if (!groupedByGroupAndProject[key]) {
                    // Determine if this is a custom group
                    const isCustomGroup =
                        group.groupType !== undefined &&
                        group.groupType !== GROUP_TYPES.PRODUCT &&
                        group.groupType !== GROUP_TYPES.INTERNAL;

                    groupedByGroupAndProject[key] = {
                        groupId: group.id,
                        groupName: group.name,
                        isCustomGroup,
                        instanceName,
                        nickname: nickname || undefined,
                        projectId: project?.id,
                        projectName: project?.name,
                        roles: []
                    };
                }

                // Add the role to this group/project combination
                const instanceRole: InstanceRole = {
                    id: role.id,
                    name: role.name,
                    displayName: role.name, // Use name as displayName since InstanceRole requires it
                    isInstanceRole: role.isInstanceRole
                };

                groupedByGroupAndProject[key].roles.push(instanceRole);
            });

            // Initialize an instance access array if it doesn't exist
            if (!accessDetails[productId].instanceAccess[instanceId]) {
                accessDetails[productId].instanceAccess[instanceId] = [];
            }

            // Add all grouped access detail rows for this instance
            Object.values(groupedByGroupAndProject).forEach((accessRow) => {
                accessDetails[productId].instanceAccess[instanceId].push(accessRow);
            });
        });
    });

    return accessDetails;
};

/**
 * Extracts and deduplicates user groups from access details, aggregating all unique projectIds/projectNames per group.
 * This is used when creating invitations to prepare the userGroups array and analytics data.
 *
 * @param accessDetails - AccessDetailsByProductId structure containing all access information
 * @returns Array of UserGroupWithProjectDetails with aggregated project information for analytics
 */
export const extractRoleGroups = (accessDetails: AccessDetailsByProductId): UserGroupWithProjectDetails[] => {
    const groups: UserGroupWithProjectDetails[] = [];
    Object.keys(accessDetails).forEach((productId) => {
        const productAccess = accessDetails[productId];
        Object.keys(productAccess.instanceAccess).forEach((instanceId) => {
            productAccess.instanceAccess[instanceId].forEach((row) => {
                if (row.groupId && row.groupName) {
                    const existingGroup = groups.find((g) => g.id === row.groupId);
                    if (!existingGroup) {
                        groups.push({
                            id: row.groupId,
                            name: row.groupName,
                            // keep single projectId/projectName for backwards compatibility
                            projectId: row.projectId,
                            projectName: row.projectName,
                            // aggregate all unique projectIds/projectNames for analytics
                            projectIds: row.projectId ? [row.projectId] : [],
                            projectNames: row.projectName ? [row.projectName] : []
                        });
                    } else {
                        // update scalar fields if missing
                        if (!existingGroup.projectId && row.projectId) {
                            existingGroup.projectId = row.projectId;
                        }
                        if (!existingGroup.projectName && row.projectName) {
                            existingGroup.projectName = row.projectName;
                        }
                        // ensure arrays exist
                        if (!existingGroup.projectIds) {
                            existingGroup.projectIds = [];
                        }
                        if (!existingGroup.projectNames) {
                            existingGroup.projectNames = [];
                        }
                        // add unique projectId / projectName values
                        if (row.projectId && !existingGroup.projectIds.includes(row.projectId)) {
                            existingGroup.projectIds = [...existingGroup.projectIds, row.projectId];
                        }
                        if (row.projectName && !existingGroup.projectNames.includes(row.projectName)) {
                            existingGroup.projectNames = [...existingGroup.projectNames, row.projectName];
                        }
                    }
                }
            });
        });
    });
    return groups;
};

/**
 * True when a group grants no product access at all — no product instances are attached to it.
 *
 * AccessDetailsByProductId is keyed by product id, so a group like this collapses to `{}` and
 * cannot be represented in it. Such groups are legitimate (org structure, SCIM/SSO mapping
 * targets, attach-the-product-later), so the flows that let an admin pick groups track them in a
 * second slice and merge the two wherever they ask "what did the admin actually select?".
 *
 * Before EPTS-4837 the invite form kept a flat list of chosen groups alongside the product map and
 * gated on that, so these groups worked. Collapsing to the product map alone silently dropped
 * them: the Continue/Save button could never enable and the group never reached the payload
 * (EPTS-6429).
 *
 * PRECONDITION: only call this on a map produced by a *completed, successful* lookup. An in-flight
 * or failed `getUserGroupInstances` also yields `{}`, and treating that as a confirmed answer would
 * strand a group that does have instances. Callers gate on
 * `CustomGroupAccess.accessDetailsStatus === "loaded"` before reaching here.
 */
export const grantsNoProductAccess = (accessDetails?: AccessDetailsByProductId): boolean =>
    !accessDetails || Object.keys(accessDetails).length === 0;

/**
 * Appends a group that grants no product access, deduped by id. Returns the original array
 * unchanged when the group is already present or has no id, so callers can assign the result
 * straight back into state without re-rendering for a no-op.
 */
export const addGroupWithoutProductAccess = (
    groups: UserGroupWithProjectDetails[],
    group: UserGroup | undefined
): UserGroupWithProjectDetails[] => {
    if (!group?.id || groups.some((g) => g.id === group.id)) {
        return groups;
    }

    return [...groups, { id: group.id, name: group.name }];
};

/**
 * The full set of groups an admin selected: those derived from product access rows plus those that
 * grant no product access. Deduped by id, because a group can legitimately appear in both slices
 * (it granted nothing when first picked, then had a product attached before the form was
 * submitted). Every flow that builds a `userGroups` payload should go through this rather than
 * `extractRoleGroups` alone.
 */
export const toSelectedUserGroups = ({
    accessDetailsByProductId,
    groupsWithoutProductAccess = []
}: {
    accessDetailsByProductId: AccessDetailsByProductId;
    groupsWithoutProductAccess?: UserGroupWithProjectDetails[];
}): UserGroupWithProjectDetails[] => {
    // extractRoleGroups builds a fresh array, so appending to it here is safe.
    const groups = extractRoleGroups(accessDetailsByProductId);
    const seenGroupIds = new Set(groups.map((g) => g.id));

    groupsWithoutProductAccess.forEach((group) => {
        if (group.id && !seenGroupIds.has(group.id)) {
            seenGroupIds.add(group.id);
            groups.push(group);
        }
    });

    return groups;
};
